import { useRef, useState, useCallback } from "react";

/* ---------- Utility: Black Video Track ---------- */
const createBlackVideoTrack = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 640;
  canvas.height = 480;

  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "black";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Draw text to indicate camera is off
  ctx.fillStyle = "white";
  ctx.font = "40px Arial";
  ctx.textAlign = "center";
  ctx.fillText("CAMERA OFF", 320, 240);

  return canvas.captureStream(5).getVideoTracks()[0];
};

export const useMedia = () => {
  /* ---------- State (For UI Rendering) ---------- */
  const [localStream, setLocalStreamState] = useState(null);
  const [remoteStream, setRemoteStreamState] = useState(null);
  const [recording, setRecording] = useState(null); // 'video', 'audio', or null

  /* ---------- Refs (For Logic/Cleanup) ---------- */
  const localStreamRef = useRef(null);
  const cameraTrackRef = useRef(null);
  const recorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  /* ---------- Actions ---------- */

  // ✅ FIX: Explicitly expose a setter that updates both Ref and State
  // This satisfies the 'setLocalStream' dependency in useCallSocket.js
  const setLocalStream = useCallback((stream) => {
    localStreamRef.current = stream;
    if (stream) {
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) cameraTrackRef.current = videoTrack;
    }
    setLocalStreamState(stream);
  }, []);

  const setRemoteStream = useCallback((stream) => {
    setRemoteStreamState(stream);
  }, []);

  // ✅ Init Camera + Mic (ONCE)
  const initLocalMedia = useCallback(async () => {
    if (localStreamRef.current) return localStreamRef.current;

    try {
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: { width: 640, height: 480 } // Conserving bandwidth
        });

        setLocalStream(stream);
        return stream;
    } catch (err) {
        console.error("Error accessing media:", err);
        return null;
    }
  }, [setLocalStream]);

  /* ---------- Video Toggle (Black Frame Logic) ---------- */
  const toggleVideoTrack = useCallback(async (sender, enabled) => {
    if (!sender) return;

    if (!enabled) {
      console.log("🚫 Muting Video: Sending Black Frames");
      // Replace camera with inert black frames
      const blackTrack = createBlackVideoTrack();
      await sender.replaceTrack(blackTrack);

      // Stop real camera hardware to turn off the light
      cameraTrackRef.current?.stop();
      // We don't nullify the stream, just the track reference
    } else {
      console.log("📸 Unmuting Video: Restarting Camera");
      // Re-acquire camera
      try {
          const camStream = await navigator.mediaDevices.getUserMedia({ video: true });
          const camTrack = camStream.getVideoTracks()[0];

          cameraTrackRef.current = camTrack;
          await sender.replaceTrack(camTrack);
          
          // Optional: Update local stream tracks if needed for local UI mirroring
          if (localStreamRef.current) {
              const oldTrack = localStreamRef.current.getVideoTracks()[0];
              if (oldTrack) localStreamRef.current.removeTrack(oldTrack);
              localStreamRef.current.addTrack(camTrack);
          }
      } catch (e) {
          console.error("Failed to restart camera:", e);
      }
    }
  }, []);

  /* ---------- Cleanup ---------- */
  const stopLocalMedia = useCallback(() => {
    if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    localStreamRef.current = null;
    cameraTrackRef.current = null;
    setLocalStreamState(null);
    setRemoteStreamState(null);
  }, []);

  /* ---------- Recording ---------- */
  const startRecording = useCallback((type = "video") => {
    // Determine which stream to record (default to remote for viewers)
    const streamToRecord = type === "local" ? localStreamRef.current : remoteStream;

    if (!streamToRecord) {
        console.warn("❌ No stream available to record");
        return;
    }

    recordedChunksRef.current = [];
    try {
        // Try efficient codecs first
        const options = { mimeType: "video/webm;codecs=vp9,opus" };
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
             delete options.mimeType; // Fallback to browser default
        }
        recorderRef.current = new MediaRecorder(streamToRecord, options);
    } catch (e) {
        recorderRef.current = new MediaRecorder(streamToRecord);
    }

    recorderRef.current.ondataavailable = (e) => {
      if (e.data.size > 0) recordedChunksRef.current.push(e.data);
    };

    recorderRef.current.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `recording-${Date.now()}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    recorderRef.current.start();
    setRecording(type);
    console.log(`🎙️ Recording Started: ${type}`);
  }, [remoteStream]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
      setRecording(null);
      console.log("⏹️ Recording Stopped");
    }
  }, []);

  return {
    // Data
    localStream, // State (for UI)
    localStreamRef, // Ref (for logic)
    remoteStream,
    recording,

    // Actions
    setLocalStream,  // ✅ EXPORTED to fix TypeError
    setRemoteStream,
    initLocalMedia,
    toggleVideoTrack, // Enhanced toggle
    stopLocalMedia,

    // Recording
    startRecording,
    stopRecording,
  };
};